import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal, IonPage } from "@ionic/react";
import { Helmet } from "react-helmet";

const allEmojis = [
    "🦁", "🐶", "🐱", "🐰", "🐭", "🐸", "🦉", "🦊", "🐯", "🐻", "🐷", "🐵", "🐢", "🐦", "🐙", "🐧",
    "🚗", "🚀", "🚲", "✈️", "🚢", "🚜", "🏠", "🏡", "🏫", "🏥", "🎈", "🎸", "🎤", "🎭", "🎮", "⚽",
    "🏀", "🏈", "🎾", "🥎", "🎯", "🛴", "🛵", "🚁", "🚂", "🚆", "📕", "📗", "📘", "📙", "📚", "📖"
];

const positions = [
    { number: 1, text: "1st", word: "First" },
    { number: 2, text: "2nd", word: "Second" },
    { number: 3, text: "3rd", word: "Third" },
    { number: 4, text: "4th", word: "Fourth" },
    { number: 5, text: "5th", word: "Fifth" },
    { number: 6, text: "6th", word: "Sixth" },
    { number: 7, text: "7th", word: "Seventh" },
    { number: 8, text: "8th", word: "Eighth" },
    { number: 9, text: "9th", word: "Nineth" },
    { number: 10, text: "10th", word: "Tenth" },
];

const IdentifyPosition: React.FC = () => {
    const [emojiRow, setEmojiRow] = useState<string[]>([]);
    const [question, setQuestion] = useState<string>("");
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewRound();
        document.title = "Object from Position Worksheet | Ordinal Numbers for UKG";
    }, []);

    const generateNewRound = () => {
        // Generate a random set of 8 emojis
        const newEmojiSet = [...allEmojis].sort(() => 0.5 - Math.random()).slice(0, 10);

        // Choose a random position
        const posIndex = Math.floor(Math.random() * positions.length);
        const posData = positions[posIndex];

        // Randomly decide whether to use "3rd" or "Third"
        const positionText = Math.random() < 0.5 ? posData.text : posData.word;

        // Get the correct answer
        const correctAnimal = newEmojiSet[posIndex];

        // Generate 2 random incorrect options
        let randomOptions = [...newEmojiSet].filter((emoji) => emoji !== correctAnimal);
        randomOptions = randomOptions.sort(() => 0.5 - Math.random()).slice(0, 2);

        // Mix correct answer with incorrect ones
        const answerChoices = [...randomOptions, correctAnimal].sort(() => 0.5 - Math.random());

        setEmojiRow(newEmojiSet);
        setQuestion(`Which object is ${positionText} in the row?`);
        setCorrectAnswer(correctAnimal);
        setOptions(answerChoices);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewRound()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG kids to identify objects based on their position using ordinal numbers (1st, 2nd, 3rd). Download free printable worksheets to master position-based learning." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/ordinal-numbers/object-from-position" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord ordinalNumber matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Object from Position</h1>

                                    <p>
                                        Understanding where something is placed in a line or sequence is an essential early math concept. Our <strong>Object from Position worksheets</strong> for UKG students focus on identifying the <strong>1st, 2nd, 3rd, etc.</strong> object in a row using <strong>ordinal numbers</strong>.
                                    </p>

                                    <h2>🔍 What Are Ordinal Numbers?</h2>
                                    <p>
                                        Ordinal numbers describe the <strong>position or order</strong> of things. Unlike cardinal numbers (1, 2, 3), ordinal numbers are used to say which item comes <em>first</em>, <em>second</em>, <em>third</em>, and so on.
                                    </p>

                                    <p><strong>Examples:</strong></p>
                                    <ul>
                                        <li>The <strong>1st</strong> object is a ball</li>
                                        <li>The <strong>3rd</strong> object is a book</li>
                                        <li>The <strong>5th</strong> object is a pencil</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand and recognize ordinal numbers (1st to 10th)</li>
                                        <li>Identify objects based on their position</li>
                                        <li>Enhance observation and sequencing skills</li>
                                        <li>Strengthen spatial awareness and logic</li>
                                    </ul>

                                    <h2>📝 Activity Examples</h2>
                                    <ul>
                                        <li>Look at a row of pictures and select the object in the given position</li>
                                        <li>Color the 2nd/5th/last object in a line</li>
                                        <li>Match ordinal numbers to the correct objects</li>
                                        <li>Drag-and-drop style games (for digital learners)</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>👩‍🏫 Teaching Tips</h2>
                                    <ul>
                                        <li>Use toys or blocks in a line and ask, “Which is 3rd from the left?”</li>
                                        <li>Play games like “Guess the Position” or “Find the 5th toy”</li>
                                        <li>Use classroom or home routines to reinforce positions (e.g., “Who is sitting first?”)</li>
                                        <li>Introduce ordinal words both in conversation and with flashcards</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Object from Position</h1>

                                <p>
                                    Understanding where something is placed in a line or sequence is an essential early math concept. Our <strong>Object from Position worksheets</strong> for UKG students focus on identifying the <strong>1st, 2nd, 3rd, etc.</strong> object in a row using <strong>ordinal numbers</strong>.
                                </p>

                                <h2>🔍 What Are Ordinal Numbers?</h2>
                                <p>
                                    Ordinal numbers describe the <strong>position or order</strong> of things. Unlike cardinal numbers (1, 2, 3), ordinal numbers are used to say which item comes <em>first</em>, <em>second</em>, <em>third</em>, and so on.
                                </p>

                                <p><strong>Examples:</strong></p>
                                <ul>
                                    <li>The <strong>1st</strong> object is a ball</li>
                                    <li>The <strong>3rd</strong> object is a book</li>
                                    <li>The <strong>5th</strong> object is a pencil</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand and recognize ordinal numbers (1st to 10th)</li>
                                    <li>Identify objects based on their position</li>
                                    <li>Enhance observation and sequencing skills</li>
                                    <li>Strengthen spatial awareness and logic</li>
                                </ul>

                                <h2>📝 Activity Examples</h2>
                                <ul>
                                    <li>Look at a row of pictures and select the object in the given position</li>
                                    <li>Color the 2nd/5th/last object in a line</li>
                                    <li>Match ordinal numbers to the correct objects</li>
                                    <li>Drag-and-drop style games (for digital learners)</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👩‍🏫 Teaching Tips</h2>
                                <ul>
                                    <li>Use toys or blocks in a line and ask, “Which is 3rd from the left?”</li>
                                    <li>Play games like “Guess the Position” or “Find the 5th toy”</li>
                                    <li>Use classroom or home routines to reinforce positions (e.g., “Who is sitting first?”)</li>
                                    <li>Introduce ordinal words both in conversation and with flashcards</li>
                                </ul>
                            </article>
                            <div className="optionImageDiv">
                                {emojiRow.map((emoji, index) => (
                                    <span key={index} style={{ margin: "10px" }} onClick={() => checkAnswer(emoji)}>
                                        {emoji}
                                    </span>
                                ))}
                            </div>
                            <h3>{question}</h3>
                            <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default IdentifyPosition;
